import type { ListType } from '@/models/app';
import type { Venue, VenueSelection } from '@/models/venue';
import type { CustomLocationType } from '@/models/address';

export const ORIGINAL_ADDRESS_ITEM: ListType = {
  key: ':',
  label: 'Original Address',
};

export const LAST_SUCCESSFUL_PICKUP_KEY = 'last-successful-pickup';
export const SAVED_LOCATION_KEY = 'saved-location';

export const parseEntranceKey = (key: string) => {
  const [venueId, zoneId, locationId] = key.split(':');
  return {
    venueId,
    zoneId,
    locationId,
  };
};

export const getEntranceKeyFromAddress = (address: Pick<CustomLocationType, 'venueId' | 'zoneId' | 'locationId'>) => {
  return `${address.venueId ?? ''}:${address.zoneId ?? ''}:${address.locationId ?? ''}`;
};

export const buildEntranceOptions = (venues: Venue[] | null | undefined): ListType[] | null => {
  if (!venues?.length) {
    return null;
  }

  const allOptions = [ORIGINAL_ADDRESS_ITEM];

  for (const venue of venues) {
    if (venue.zones?.length) {
      const options = venue.zones.flatMap(zone =>
        zone.locations?.length
          ? zone.locations.map((loc) => ({
              key: `${venue.id}:${zone.id}:${loc.id}`,
              label: `${venue.name}, ${zone.name}, ${loc.name}`,
            }))
          : {
              key: `${venue.id}:${zone.id}:`,
              label: `${venue.name}, ${zone.name}`,
            }
      );

      allOptions.push(...options);
    } else if (venue.locations?.length) {
      const options = venue.locations.map((loc) => ({
        key: `${venue.id}::${loc.id}`,
        label: loc.name === venue.name ? loc.name : `${venue.name}, ${loc.name}`,
      }));

      allOptions.push(...options);
    }
  }

  return allOptions.length > 1 ? allOptions : null;
};

export type EntranceInfo = Pick<VenueSelection, 'venue' | 'zone' | 'location' | 'name'>;

export const getZoneLocationByEntrance = (
  entranceOption: string,
  venues: Venue[] | null | undefined,
  entranceOptions: ListType[] | null,
): EntranceInfo | null => {
  if (!venues?.length || entranceOption === ORIGINAL_ADDRESS_ITEM.key) {
    return null;
  }

  const option = entranceOptions?.find(
    (opt) => opt.key === entranceOption
  );

  if (!option) {
    return null;
  }

  const { venueId, zoneId, locationId } = parseEntranceKey(option.key);

  const venue = venues.find(item => item.id === venueId);
  const zone = venue?.zones?.find(item => item.id === zoneId);
  const location = (zone?.locations || venue?.locations)?.find(
    (loc) => loc.id === locationId
  );

  return {
    venue,
    zone,
    location,
    name: option.label,
  };
};
